import React, { useCallback, useLayoutEffect } from 'react';
import { SectionHeader } from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import {
  CheckoutLayout,
  CheckoutLayoutBlock,
  CheckoutLayoutColumn,
  LoadingAnimation,
  NotFoundView,
  PaymentBlockCardIcons,
  SignedOutView,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateToMainTabs } from '../../navigation';
import type { CheckoutScreenProps } from '../../navigation/AppNavigation.props';
import { useReorderState } from '../ReorderingScreen';
import {
  CheckoutContextProvider,
  useCheckoutContext,
} from './CheckoutProvider';
import {
  CheckoutAddressForm,
  CheckoutBlockVStack,
  CheckoutWrapper,
  ConnectedCheckoutDeliveryTipBlock,
  ConnectedCheckoutExtrasBlock,
  ConnectedCheckoutHeader,
  ConnectedCheckoutOrderBlock,
  ConnectedCheckoutPaymentBlockAvailableCredit,
  ConnectedCheckoutPaymentBlockCreditCardPicker,
  ConnectedCheckoutPaymentBlockPromoCode,
  ConnectedCheckoutRestaurantTipBlock,
  ConnectedCustomerOptions,
  ConnectedDeliveryOptions,
  ConnectedFutureDateModal,
  ConnectedOutpostOptions,
  ConnectedPickupOptions,
  ConnectedRewardExceptionModal,
} from './components';
import {
  useConnectedCheckoutViewEventEffect,
  useConnectedDeliveryReorder,
  useSyncDeliveryOrderDetails,
} from './hooks';

export const CheckoutScreen = (props: CheckoutScreenProps) => {
  return (
    <CheckoutContextProvider>
      <CheckoutScreenContent {...props} />
    </CheckoutContextProvider>
  );
};

const CheckoutScreenContent = (props: CheckoutScreenProps) => {
  const { navigation } = props;
  const { t } = useLocalizationContext();

  const {
    customer,
    cart,
    cartOrderType,
    isCartValid,
    orderAddress,
    addressId,
    isCartPickup,
    isCartOutpost,
    isCartDelivery,
    hasCompletedOrderAddress,
    setPayload,
  } = useCheckoutContext();

  // ───── Render Control ───────────────────────────────────────────
  const { reordering: isReordering } = useReorderState();
  const isLoggedIn = useIsLoggedIn();

  // ───── Checkout Title ───────────────────────────────────────────
  const title = t('checkout.finish-order', { cartOrderType });

  useLayoutEffect(() => {
    navigation.setOptions({ title });
  }, [navigation, title]);

  // ───── Effects ──────────────────────────────────────────────────
  useConnectedDeliveryReorder();
  useSyncDeliveryOrderDetails({
    cart,
    addressId,
    setPayload,
  });

  // ───── Telemetry ────────────────────────────────────────────────
  useConnectedCheckoutViewEventEffect();

  // ───── Not Found View ───────────────────────────────────────────
  const navigateToMainTabs = useNavigateToMainTabs(navigation);
  const dismissAndNavigateToLocations = useCallback(() => {
    void navigateToMainTabs('MenuTab', { screen: 'Locations' });
  }, [navigateToMainTabs]);

  // ───── Logged Out -> Login ──────────────────────────────────────
  if (!isLoggedIn) {
    return <SignedOutView />;
  }

  // ───── Render Control ───────────────────────────────────────────
  if (isReordering) return <LoadingAnimation />;

  // ───── Empty Cart -> Location ───────────────────────────────────
  if (!isCartValid) {
    return (
      <NotFoundView
        btnLabel={t('not-found-view.empty-cart')}
        description={t('bag.empty')}
        onBtnPress={dismissAndNavigateToLocations}
      />
    );
  }

  if (isCartDelivery && !hasCompletedOrderAddress) {
    return <CheckoutAddressForm orderAddress={orderAddress ?? undefined} />;
  }

  return (
    <CheckoutWrapper>
      {/* Header */}
      <ConnectedCheckoutHeader />

      <CheckoutLayout>
        <CheckoutLayoutColumn>
          {/* Details Block */}
          <CheckoutLayoutBlock>
            <SectionHeader
              heading={t('checkout.block-details-title', { cartOrderType })}
            />
            <CheckoutBlockVStack>
              {isCartPickup ? <ConnectedPickupOptions /> : null}
              {isCartOutpost ? <ConnectedOutpostOptions /> : null}
              {isCartDelivery ? <ConnectedDeliveryOptions /> : null}
              {customer ? <ConnectedCustomerOptions /> : null}
            </CheckoutBlockVStack>
          </CheckoutLayoutBlock>

          {/* Payment Block */}
          <CheckoutLayoutBlock>
            <PaymentBlockCardIcons />
            <CheckoutBlockVStack>
              <ConnectedCheckoutPaymentBlockCreditCardPicker />
              <ConnectedCheckoutPaymentBlockAvailableCredit />
              <ConnectedCheckoutPaymentBlockPromoCode />
            </CheckoutBlockVStack>
          </CheckoutLayoutBlock>

          {/* Pickup Block */}
          {isCartPickup ? <ConnectedCheckoutRestaurantTipBlock /> : null}

          {/* Delivery Block */}
          {isCartDelivery ? (
            <>
              <ConnectedCheckoutDeliveryTipBlock />
              <ConnectedCheckoutExtrasBlock />
            </>
          ) : null}
        </CheckoutLayoutColumn>

        {/* OrderBlock */}
        <CheckoutLayoutColumn>
          <ConnectedCheckoutOrderBlock />
        </CheckoutLayoutColumn>
      </CheckoutLayout>

      {/* Modals */}
      <ConnectedRewardExceptionModal />
      <ConnectedFutureDateModal />
    </CheckoutWrapper>
  );
};
